/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-21 15:23:02                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-03-05 14:54:15                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Styles
  * Contexts
  * DrawerLayout: layout that contain form in a drawer
    * closeDrawer - close drawer instance
    * drawerReducer - manage drawer event
    * useReducer - register drawer instance
*/

/* Imports */
import { createContext, useContext, useEffect, useReducer, useRef } from "react";
import { Drawer, IconButton, Typography } from "@material-tailwind/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
/***/

/* Styles */
import "./DrawerLayout.scss";
/***/

/* Contexts */
const DrawerLayoutDispatchContext = createContext<any>(null);
/***/

const FIRST_FIELD = 'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not(:disabled), textarea:not(:disabled), select:not(:disabled)';

function DrawerBody({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      ref.current?.querySelector<HTMLElement>(FIRST_FIELD)?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  return <div ref={ref}>{children}</div>;
}

/*
 * DrawerLayout: layout that contain form in a drawer
 * provide a context that give a dispatch function to 
 * each child component to open the drawer with a specific
 * form or close the drawer
*/
const DrawerLayout = ({children}: any) => {

  /**
  * drawerReducer - manage drawer event
  * @param state - stack of drawers
  * @param action - action instance 
  */
  const drawerReducer = (state: any[], action: any) => {
    switch (action.type) {
    case "open": {
      return [...state, { title: action.title, content: action.content }];
    }
    case "close": {
      return state.slice(0, -1);
    }
    default: {
      throw Error("Unknown action: " + action.type);
    }
    }
  };
  /***/

  /**
  * useReducer - register drawer instance
  */
  const [drawers, dispatch] = useReducer(
    drawerReducer, []
  );
  /***/

  /**
  * closeDrawer - close drawer instance 
  */
  const closeDrawer = () => {
    dispatch({ type: "close" });
  };
  /***/

  return (
    <div className="drawer-layout">
      <DrawerLayoutDispatchContext.Provider value={dispatch}>
        {children}

        {drawers.map((drawer, index) => (
          <Drawer key={index}
                  placement="right"
                  open={true}
                  size={window.innerWidth}
                  transition={{ type: "tween", duration: 0.48, ease: [0.16, 1, 0.3, 1] }}
                  onClose={closeDrawer}
                  overlay={false}
                  dismiss={{
                    enabled: false
                  }}
                  className={`easytech-drawer drawer-open`}>
            <div className="drawer-header">
              <Typography className="text-lg">{drawer.title || ""}</Typography>

              <IconButton variant="text" color="blue-gray" onClick={closeDrawer}>
                <XMarkIcon className="w-[24px]" />
              </IconButton>
            </div>
                    
            <DrawerBody>{drawer.content}</DrawerBody>
          </Drawer>
        ))}
      </DrawerLayoutDispatchContext.Provider>
    </div>
  );
};
/***/

export function useDrawerLayoutDispatch() {
  return useContext(DrawerLayoutDispatchContext);
}


export default DrawerLayout;
